import axios from 'axios'

/* @ngInject */
function multipartUploadService (NewAssetService, Utils, UploadQueueService, CONSTANTS, MimeService) {
  const service = {
    uploadFile
  }
  return service

  async function uploadFile (file, options = {}) {
    const FILE_CHUNK_SIZE = Utils.defineOptimalChunkSize(file.size)
    const queue = UploadQueueService.createQueue()
    let s3KeySuffix = ''

    if (options?.generateThumbnailSync) {
      s3KeySuffix = '-sync'
    }

    try {
      const fileType = MimeService.getType(file.name) || file.type
      const extension = file.name.split('.').pop()
      const multipartUploadData = await NewAssetService.startMultipartUpload({
        fileSize: file.size,
        contentType: fileType,
        extension,
        s3KeySuffix,
        existentS3Key: options?.existentS3Key
      })
      file.s3Key = multipartUploadData.Key

      return new Promise((resolve) => {
        const NUM_CHUNKS = Math.floor(file.size / FILE_CHUNK_SIZE) + 1
        const uploadedParts = []
        const uploadProgress = {}
        const queueConfig = {
          maxConcurrency: CONSTANTS.FILE_UPLOAD_MAX_CONCURRENCY,
          handler: uploadHandler,
          callback: (response, uploadedItem) => {
            uploadedParts.push({
              ETag: response.headers.etag,
              PartNumber: uploadedItem.partNumber
            })

            if (uploadedParts.length === NUM_CHUNKS) { // when all parts have been uploaded
              // reorder uploadedParts
              uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber)

              NewAssetService.completeMultipartUpload(uploadedItem.key, uploadedItem.uploadId, uploadedParts)
                .then(() => {
                  if (options?.skipAssetCreation) {
                    resolve({
                      ...file,
                      fileType
                    })

                    return
                  }

                  NewAssetService.createAsset({
                    s3Key: uploadedItem.key,
                    name: file.name,
                    size: file.size,
                    contentType: fileType,
                    isOrganized: Boolean(options?.asset?.isOrganized),
                    folderId: options?.asset?.folderId
                  }, { generateThumbnailSync: Boolean(options?.generateThumbnailSync) }).then(resolve)
                })
            }
          }
        }
        const onUploadProgress = (fileToUpload, event) => {
          uploadProgress[fileToUpload.partNumber] = (event.loaded * 100) / event.total

          if (typeof options.onUploadProgress === 'function') {
            let progress = 0
            Object.keys(uploadProgress).forEach((key) => {
              progress += uploadProgress[key]
            })

            options.onUploadProgress(file, Math.round(progress / NUM_CHUNKS))
          }
        }

        for (let index = 1; index <= NUM_CHUNKS; index++) {
          const start = (index - 1) * FILE_CHUNK_SIZE
          const end = index * FILE_CHUNK_SIZE
          const blob = index < NUM_CHUNKS ? file.slice(start, end) : file.slice(start)

          // add to queue
          const chunkToUpload = {
            onUploadProgress,
            blob,
            file,
            key: multipartUploadData.Key,
            uploadId: multipartUploadData.UploadId,
            partNumber: index,
            type: fileType
          }
          queue.add(chunkToUpload)
        }

        queue.process(queueConfig)
      })
    } catch (err) {
      console.error(err) // sometimes errors inside async/await function might be supressed. This is why this console.log is here
      throw err
    }
  }

  async function uploadHandler (fileToUpload, queue) {
    const uploadUrl = await NewAssetService.getMultipartUploadUrl(fileToUpload.key, fileToUpload.uploadId, fileToUpload.partNumber)
    const abortController = new AbortController()

    return axios.put(uploadUrl, fileToUpload.blob, {
      headers: {
        'Content-Type': fileToUpload.type
      },
      signal: abortController.signal,
      onUploadProgress: (event) => {
        fileToUpload.onUploadProgress(fileToUpload, event)
        fileToUpload.abortUpload = abortController.abort.bind(abortController)
        abortController.signal.onabort = function () {
          queue.clear()
        }
      }
    })
  }
}

export { multipartUploadService }
